interface ColorStop {
  color: string;
  position: number;
}

interface GradientInfo {
  type:
    | "linear-gradient"
    | "radial-gradient"
    | "conic-gradient"
    | "repeating-linear-gradient"
    | "repeating-radial-gradient"
    | "repeating-conic-gradient";
  direction?: string;
  colorStops: ColorStop[];
  rawValue: string;
}

function parseGradient(gradientString?: string): GradientInfo | null {
  if (!gradientString || typeof gradientString !== "string") {
    return null;
  }

  // Odstranění white-space a normalizace
  const normalized = gradientString.trim();

  // Regex pro různé typy gradientů
  const gradientRegex =
    /(linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient|repeating-conic-gradient)\s*\(\s*(.*)\s*\)/i;

  const match = normalized.match(gradientRegex);
  if (!match) {
    return null;
  }

  const [, type, content] = match;
  if(!type || !content) {
    return null
  }
  const gradientType = type.toLowerCase() as GradientInfo["type"];

  // Parsování obsahu gradientu
  const parts = parseGradientContent(content);

  let direction: string | undefined;
  let colorStops: ColorStop[] = [];

  if (gradientType.includes("linear")) {
    const result = parseLinearGradient(parts);
    direction = result.direction;
    colorStops = result.colorStops;
  } else if (gradientType.includes("radial")) {
    const result = parseRadialGradient(parts);
    direction = result.direction;
    colorStops = result.colorStops;
  } else if (gradientType.includes("conic")) {
    const result = parseConicGradient(parts);
    direction = result.direction;
    colorStops = result.colorStops;
  }

  return {
    type: gradientType,
    direction,
    colorStops,
    rawValue: gradientString,
  };
}

function parseGradientContent(content: string): string[] {
  const parts: string[] = [];
  let current = "";
  let parenDepth = 0;
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < content.length; i++) {
    const char = content[i];

    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true;
      quoteChar = char;
    } else if (char === quoteChar && inQuotes) {
      inQuotes = false;
      quoteChar = "";
    }

    if (!inQuotes) {
      if (char === "(") {
        parenDepth++;
      } else if (char === ")") {
        parenDepth--;
      } else if (char === "," && parenDepth === 0) {
        parts.push(current.trim());
        current = "";
        continue;
      }
    }

    current += char;
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  return parts;
}

function parseLinearGradient(parts: string[]): {
  direction?: string;
  colorStops: ColorStop[];
} {
  let direction: string | undefined;
  const colorStops: ColorStop[] = [];
  let startIndex = 0;

  // Kontrola prvního parametru - může být směr
  if (parts.length > 0) {
    const firstPart = parts[0];
    if (isDirection(firstPart)) {
      direction = firstPart;
      startIndex = 1;
    }
  }

  // Parsování color stops
  for (let i = startIndex; i < parts.length; i++) {
    const colorStop = parseColorStop(parts[i], i, parts.length);
    if (colorStop) {
      colorStops.push(colorStop);
    }
  }

  return { direction, colorStops };
}

function parseRadialGradient(parts: string[]): {
  direction?: string;
  colorStops: ColorStop[];
} {
  let direction: string | undefined;
  const colorStops: ColorStop[] = [];
  let startIndex = 0;

  // Pro radial gradient může být první část shape/size/position
  if (parts.length > 0) {
    const firstPart = parts[0];
    if (isRadialDirection(firstPart)) {
      direction = firstPart;
      startIndex = 1;
    }
  }

  // Parsování color stops
  for (let i = startIndex; i < parts.length; i++) {
    const colorStop = parseColorStop(parts[i], i, parts.length);
    if (colorStop) {
      colorStops.push(colorStop);
    }
  }

  return { direction, colorStops };
}

function parseConicGradient(parts: string[]): {
  direction?: string;
  colorStops: ColorStop[];
} {
  let direction: string | undefined;
  const colorStops: ColorStop[] = [];
  let startIndex = 0;

  // Pro conic gradient může být první část angle/position
  if (parts.length > 0) {
    const firstPart = parts[0];
    if (isConicDirection(firstPart)) {
      direction = firstPart;
      startIndex = 1;
    }
  }

  // Parsování color stops
  for (let i = startIndex; i < parts.length; i++) {
    const colorStop = parseColorStop(parts[i], i, parts.length);
    if (colorStop) {
      colorStops.push(colorStop);
    }
  }

  return { direction, colorStops };
}

function parseColorStop(
  stopString: string | undefined,
  index: number,
  count: number
): ColorStop | null {
  if(stopString === undefined) {
    return null
  }
  const trimmed = stopString.trim();

  // Regex pro color stop: barva následovaná volitelnou pozicí
  const colorStopRegex =
    /^(.+?)\s+([\d.]+%|[\d.]+(?:px|em|rem|vh|vw|deg))(?:\s+([\d.]+%|[\d.]+(?:px|em|rem|vh|vw|deg)))?$/;
  const match = trimmed.match(colorStopRegex);

  if (match) {
    const [, color, position1, position2] = match;
    const position = position2 ? `${position1} ${position2}` : position1;

    if(color === undefined || position === undefined) {
      return null
    }

    return {
      color: color.trim(),
      position: parseInt(position),
    };
  }

  // Pokud není pozice specifikována, celý string je barva
  if (isColor(trimmed)) {
    return {
      color: trimmed,
      position: (index * 100) / count,
    };
  }

  return null;
}

function isDirection(value: string | undefined): boolean {
  if(value === undefined) {
    return false
  }

  const directions = [
    "to top",
    "to bottom",
    "to left",
    "to right",
    "to top left",
    "to top right",
    "to bottom left",
    "to bottom right",
  ];

  return (
    directions.includes(value.toLowerCase()) ||
    /^[\d.-]+deg$/.test(value) ||
    /^[\d.-]+turn$/.test(value) ||
    /^[\d.-]+rad$/.test(value)
  );
}

function isRadialDirection(value: string | undefined): boolean {
  if(value === undefined) {
    return false
  }

  return (
    /^(circle|ellipse)/.test(value.toLowerCase()) ||
    /^(closest-side|closest-corner|farthest-side|farthest-corner)/.test(
      value.toLowerCase()
    ) ||
    /^at\s/.test(value.toLowerCase()) ||
    /^\d+(%|px|em|rem)/.test(value)
  );
}

function isConicDirection(value: string | undefined): boolean {
  if(value === undefined) {
    return false
  }

  return (
    /^from\s[\d.-]+(deg|turn|rad)/.test(value.toLowerCase()) ||
    /^at\s/.test(value.toLowerCase())
  );
}

function isColor(value: string): boolean {
  if (
    value.toLowerCase() === "transparent" ||
    value.toLowerCase() === "currentcolor"
  ) {
    return true;
  }

  // Základní kontrola, zda string může být barva
  const colorPatterns = [
    /^#[0-9a-f]{3,8}$/i, // hex
    /^rgb\(/i, // rgb()
    /^rgba\(/i, // rgba()
    /^hsl\(/i, // hsl()
    /^hsla\(/i, // hsla()
    /^[a-z]+$/i, // named colors
  ];

  return colorPatterns.some((pattern) => pattern.test(value));
}

// Příklady použití:
/*
const examples = [
  'linear-gradient(45deg, red 0%, blue 50%, green 100%)',
  'linear-gradient(to right, #ff0000, #00ff00 25%, #0000ff)',
  'radial-gradient(circle, red, blue)',
  'conic-gradient(from 45deg, red, blue, green)'
];

examples.forEach(gradient => {
  console.log('\nGradient:', gradient);
  console.log('Parsed:', parseGradient(gradient));
});
*/

export { parseGradient, type GradientInfo, type ColorStop };
